import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { TaskType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import TaskStatusBadge from '../../shared/TaskStatusBadge';
import KanbanCard, { type KanbanRelatedTasksTarget } from './KanbanCard';
import { columnDroppableId } from './helpers/kanbanDropRules';

export interface KanbanColumnProps {
    status: number;
    tone: string;
    tasks: TaskType[];
    onOpenTask: (taskId: number) => void;
    onShowRelatedTasks: (target: KanbanRelatedTasksTarget) => void;
    isDropTarget?: boolean;
}

const KanbanColumn = ({
    status,
    tone,
    tasks,
    onOpenTask,
    onShowRelatedTasks,
    isDropTarget = false,
}: KanbanColumnProps) => {
    const { t } = useTranslation(['Tasks']);
    const droppableId = columnDroppableId(status);
    const { setNodeRef, isOver } = useDroppable({
        id: droppableId,
        data: { type: 'column', status },
    });

    const taskIds = tasks.map((task) => task.ID);
    const highlight = isDropTarget || isOver;

    return (
        <section
            className={[
                'task-kanban-column',
                `task-kanban-column--${tone}`,
                highlight ? 'task-kanban-column--drop-target' : undefined,
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <header className="task-kanban-column__header">
                <TaskStatusBadge status={status} />
                <span className="task-kanban-column__count">{tasks.length}</span>
            </header>

            <div
                ref={setNodeRef}
                className={[
                    'task-kanban-column__body',
                    tasks.length === 0 ? 'task-kanban-column__body--empty' : undefined,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                    {tasks.length === 0 ? (
                        <div className="task-kanban-column__empty">{t('kanbanEmpty')}</div>
                    ) : (
                        tasks.map((task) => (
                            <KanbanCard
                                key={task.ID}
                                task={task}
                                onOpen={onOpenTask}
                                onShowRelatedTasks={onShowRelatedTasks}
                            />
                        ))
                    )}
                </SortableContext>
            </div>
        </section>
    );
};

export default KanbanColumn;
